/* Write paths under a product's PDP record (designs/products-pdp.md PP13,
   PP18). The PUT answers the whole ProductPdp, which replaces the product's
   record in the store — the server is the authority. A refusal comes back as
   the server's own reason (verbatim) for the form's `.form-error`; null =
   saved. The client validated with the same shared rule first, so the
   server's answer is normally the one the reader already saw sooner (AA2). */
import type { EvidenceInput, GateReviewInput, PhaseCertificate, PhaseDeclarationInput, ProductPdp, SlotKey } from "../../../shared/product-pdp";
import type { FeedReading, FeedRef, FeedTarget } from "../../../shared/feeds";
import type { Product, ProductEdit } from "../../../shared/products";
import { announce } from "../shell/live";
import { feedKey, type FeedRowReading } from "../shell/state";
import { loadJson, type Store } from "../shell/store";
import { feedAnnouncement } from "./products/FeedRowValue";

const DECLARATION_FAILED = "Couldn't record the declaration — is the server healthy?";
const EVIDENCE_FAILED = "Couldn't record the evidence — is the server healthy?";
const REVIEW_FAILED = "Couldn't record the review — is the server healthy?";
/* kpi-feed-seam.md KF16 — the SERVER failing, not the feed (that is the
   reading's own red state); bare when no status came back at all */
const FEED_READ_FAILED = "Couldn't reach the server to read the feed";
/* products-manage.md PM2/PM3 — the product list's own writes */
const CREATE_PRODUCT_FAILED = "Couldn't create the product — is the server healthy?";
const EDIT_PRODUCT_FAILED = "Couldn't save the product — is the server healthy?";

/* one request: the Response when the server said yes, otherwise the reason
   — the server's own sentence, or `failed` (+ the status when one came) */
async function request(path: string, init: RequestInit, failed: string): Promise<Response | string> {
  let res: Response;
  try {
    res = await fetch(path, init);
  } catch {
    return failed;
  }
  if (res.ok) return res;
  try {
    const { error } = (await res.json()) as { error?: string };
    if (error) return error;
  } catch {
    /* no readable body — fall through to the status */
  }
  return `${failed} (HTTP ${res.status})`;
}

async function write(store: Store, path: string, init: RequestInit, failed: string): Promise<string | null> {
  const res = await request(path, init, failed);
  if (typeof res === "string") return res;
  store.putProductPdp((await res.json()) as ProductPdp);
  return null;
}

const json = (method: "PUT" | "POST", body: unknown): RequestInit => ({
  method,
  headers: { "content-type": "application/json" },
  body: JSON.stringify(body),
});

/* PM2: POST the name; on 201 the list is re-read (the server orders it by
   lower(name) — never spliced), the form closes and the record opens so the
   next step is Change phase…. A re-read that fails still lands the created
   product, so the record it opens is one the page knows. */
export async function createProduct(
  store: Store,
  name: string,
  /* the owning team and market (teams-and-products.md TP2, option B) */
  extra: { teamId?: string | null; market?: string | null } = {},
): Promise<string | null> {
  const res = await request("/api/intake/products", json("POST", { name, ...extra }), CREATE_PRODUCT_FAILED);
  if (typeof res === "string") return res;
  const created = (await res.json()) as Product;
  const products = await loadJson<Product[]>("/api/intake/products").catch(() => [...store.state.products, created]);
  /* the record opens only for the form that asked: a drawer hop meanwhile
     dropped the form (PM4), and the reader's navigation stands — the list
     still learns the product the server now has (Copilot review of #133) */
  if (store.state.productFormOpen !== "new") {
    store.update({ products });
    return null;
  }
  store.update({ products, productFormOpen: null });
  store.openProduct(created.id);
  return null;
}

/* PM3: rename or retire / bring back — the list is re-read like a create,
   so a renamed product sits where the server's lower(name) order puts it;
   a re-read that fails still lands the saved product in its old slot */
export async function editProduct(store: Store, productId: string, edit: ProductEdit): Promise<string | null> {
  const res = await request(`/api/intake/products/${encodeURIComponent(productId)}`, json("PUT", edit), EDIT_PRODUCT_FAILED);
  if (typeof res === "string") return res;
  const saved = (await res.json()) as Product;
  const products = await loadJson<Product[]>("/api/intake/products").catch(() =>
    store.state.products.map((p) => (p.id === saved.id ? saved : p)),
  );
  store.update({ products });
  return null;
}

export const declarePhase = (store: Store, productId: string, input: PhaseDeclarationInput): Promise<string | null> =>
  write(store, `/api/products/${encodeURIComponent(productId)}/pdp/declaration`, json("PUT", input), DECLARATION_FAILED);

const evidencePath = (productId: string, slot: SlotKey): string =>
  `/api/products/${encodeURIComponent(productId)}/pdp/slots/${encodeURIComponent(slot)}/evidence`;

/* PP13: a pointer or an estimate against one owed slot */
export const recordEvidence = (store: Store, productId: string, slot: SlotKey, input: EvidenceInput): Promise<string | null> =>
  write(store, evidencePath(productId, slot), json("PUT", input), EVIDENCE_FAILED);

/* UC step 7: clearing is a revision — the history keeps what stood */
export const clearEvidence = (store: Store, productId: string, slot: SlotKey): Promise<string | null> =>
  write(store, evidencePath(productId, slot), { method: "DELETE" }, EVIDENCE_FAILED);

/* PP7/PP8: the Admin's gate decision — the 201 answers the whole record
   (review appended, phase advanced, certificate head); a non-Admin's 403
   comes back as the server's own sentence */
export async function recordGateReview(store: Store, productId: string, input: GateReviewInput): Promise<string | null> {
  const refused = await write(store, `/api/products/${encodeURIComponent(productId)}/pdp/reviews`, json("POST", input), REVIEW_FAILED);
  /* a pass read every feed slot for the certificate (KF15) — the rows show
     those fresher log rows, not the ones held before the review */
  if (refused === null) store.refreshFeedReadings();
  return refused;
}

/* PP9: the frozen body is fetched on open; null = could not be read */
export async function fetchCertificate(productId: string, certId: string): Promise<PhaseCertificate | null> {
  try {
    const res = await fetch(`/api/products/${encodeURIComponent(productId)}/pdp/certificates/${encodeURIComponent(certId)}`);
    if (!res.ok) return null;
    return (await res.json()) as PhaseCertificate;
  } catch {
    return null;
  }
}

/* KF12: the row's Read / Refresh / Retry — the one network read a page makes.
   The answer replaces the ref's reading and is announced through #live; a
   Retry (the ref stood unreachable) stamps the client clock as `retriedAt`.
   The feed's own failure arrives as a 200 unreachable reading; only the HTTP
   layer failing lands in `httpError`, a 400 as the server's own reason (a
   source dropped from the configuration names itself). A read already in
   flight for the ref is not posted twice. */
export async function readFeed(store: Store, { ref, rowName, target }: { ref: FeedRef; rowName: string; target: FeedTarget | null }): Promise<void> {
  const empty: FeedRowReading = { reading: { state: "not-read-yet" }, inFlight: false, retriedAt: null, httpError: null };
  const held = store.state.feedReadings[feedKey(ref)] ?? empty;
  if (held.inFlight) return;
  /* the answer belongs to the record that was open when the button was
     pressed — a reader who switched records meanwhile must not see this
     row's failure (or reading) on another record sharing the ref */
  const owner = store.state.openProductId;
  const stillOpen = (): boolean => store.state.openProductId === owner;
  store.putFeedReading(ref, { ...held, inFlight: true, httpError: null });
  /* the failure keeps whatever the page holds NOW (a logged reading may have
     landed while the POST was out), not the value captured before it */
  const fail = (sentence: string): void => {
    if (stillOpen()) store.putFeedReading(ref, { ...(store.state.feedReadings[feedKey(ref)] ?? empty), inFlight: false, httpError: sentence });
  };
  let res: Response;
  let reading: FeedReading;
  try {
    res = await fetch("/api/feeds/read", json("POST", ref));
    if (!res.ok) {
      if (res.status === 400) {
        const { error } = (await res.json().catch(() => ({}))) as { error?: string };
        if (error) return fail(error);
      }
      return fail(`${FEED_READ_FAILED} (HTTP ${res.status})`);
    }
    reading = (await res.json()) as FeedReading;
  } catch {
    /* the fetch rejected, or a 200 with no readable body — no status to name */
    return fail(FEED_READ_FAILED);
  }
  if (!stillOpen()) return;
  const retriedAt = held.reading.state === "unreachable" ? new Date().toISOString() : null;
  store.putFeedReading(ref, { reading, inFlight: false, retriedAt, httpError: null });
  announce(feedAnnouncement(rowName, reading, target, retriedAt));
}
